import {cast, getParent, getSnapshot, Instance, types} from 'mobx-state-tree';
import {BidTypeEnum, UserBidData} from '@joyrideautos/auction-core/src/dtos/BidDto';
import {AuctionPath} from '@joyrideautos/auction-core/src/dtos/AuctionOccurrenceDto';
import {UIServicesAwareViewModelType} from '../common/UIServicesAwareViewModel';
import RootStoreAwareViewModel from './rootStore/RootStoreAwareViewModel';
import {makeSubscriber} from '@joyrideautos/auction-utils/src/subscriptions';
import {ItemPath} from '@joyrideautos/auction-core/src/dtos/ItemDto';

export interface RootStoreType extends Instance<typeof RootStoreAwareViewModel> {}

const BidData = types.model('BidData', {
    bidId: types.string,
    timestamp: types.string,
    type: types.maybe(types.enumeration([BidTypeEnum.AUCTION, BidTypeEnum.OFFER])),
});
export const createAuctionsWithBidsContainer = () =>
    types.optional(
        types
            .model('AuctionsWithBidsContainer', {
                // regionId/auctionId/itemId/BidData
                values: types.map(types.map(types.map(BidData))),
            })
            .actions((self) => ({
                setBidsForAuction({regionId, auctionId}: AuctionPath, bids: {[itemId: string]: UserBidData}) {
                    const bidsForRegion = self.values.get(regionId);
                    if (!bidsForRegion) {
                        self.values.set(regionId, cast({[auctionId]: bids}));
                        return;
                    }
                    const bidsForAuction = bidsForRegion.get(auctionId);
                    if (!bidsForAuction) {
                        bidsForRegion.set(auctionId, cast(bids));
                    } else {
                        bidsForAuction.replace(bids);
                    }
                },
            }))
            .views((self) => ({
                get parent(): RootStoreType & {uid?: string} {
                    return getParent(self);
                },
                get rootStore(): UIServicesAwareViewModelType {
                    return this.parent.rootStore;
                },
                get uid() {
                    return this.parent.uid;
                },
            }))
            .views((self) => {
                const subscribe =
                    (uid: string, {regionId, auctionId}: AuctionPath) =>
                    () =>
                        self.rootStore.userService.subscribeToAuctionsWithBids(
                            {uid, auctionId, regionId},
                            async (bids: {[itemId: string]: UserBidData}) => {
                                self.setBidsForAuction({regionId, auctionId}, bids);
                            }
                        );
                return {
                    subscribeToAuction(auctionPath: AuctionPath) {
                        const {regionId, auctionId} = auctionPath;
                        if (!self.uid) {
                            return () => {};
                        }
                        return makeSubscriber(
                            `auctionsWithBids-${regionId}-${auctionId}`,
                            subscribe(self.uid, auctionPath)
                        )();
                    },
                };
            })
            .views((self) => ({
                getBidsForAuction({regionId, auctionId}: AuctionPath): {[itemId: string]: UserBidData} {
                    const bidsForAuction = self.values.get(regionId)?.get(auctionId);
                    return bidsForAuction ? getSnapshot(bidsForAuction) : {};
                },
                getLastBidForItem({regionId, auctionId, itemId}: ItemPath): UserBidData | undefined {
                    const bidsForAuction = self.values.get(regionId)?.get(auctionId);
                    const bid = bidsForAuction?.get(itemId);
                    return bid ? getSnapshot(bid) : undefined;
                },
            }))
            .actions((self) => ({
                clear() {
                    self.values.clear();
                },
            })),
        {}
    );
